/**
 * First-touch signup attribution (UTM / source-medium-campaign).
 * Stored in a first-party cookie on .priorityonewashclub.com / .co.nz so the
 * WordPress promotional LP and my.* signup app share the same session UTMs.
 */

export const SIGNUP_ATTRIBUTION_COOKIE = "p1_signup_attribution";
const COOKIE_MAX_AGE_DAYS = 30;

const LINK_DEFAULT_SOURCES = new Set(["p1"]);

function getAttributionCookieDomain() {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host.endsWith("priorityonewashclub.co.nz")) {
    return ".priorityonewashclub.co.nz";
  }
  if (host.endsWith("priorityonewashclub.com")) {
    return ".priorityonewashclub.com";
  }
  return undefined;
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, maxAgeDays) {
  if (typeof document === "undefined") return;
  const maxAge = maxAgeDays * 24 * 60 * 60;
  const domain = getAttributionCookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  if (window.location.protocol === "https:") cookie += "; Secure";
  document.cookie = cookie;
}

function normalizeValue(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

/** Read utm_* and Assure-style source/medium/campaign from URLSearchParams. */
export function readAttributionFromSearchParams(searchParams) {
  if (!searchParams) return emptyAttribution();

  const get = (key) => normalizeValue(searchParams.get(key));

  return {
    utm_source: get("utm_source") || get("source"),
    utm_medium: get("utm_medium") || get("medium"),
    utm_campaign: get("utm_campaign") || get("campaign"),
    utm_content: get("utm_content") || get("content"),
    utm_term: get("utm_term") || get("term"),
  };
}

export function emptyAttribution() {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
  };
}

export function hasAttribution(attribution) {
  if (!attribution) return false;
  return Object.values(attribution).some(Boolean);
}

export function getStoredSignupAttribution() {
  try {
    const raw = readCookie(SIGNUP_ATTRIBUTION_COOKIE);
    if (!raw) return emptyAttribution();
    const parsed = JSON.parse(raw);
    return {
      utm_source: normalizeValue(parsed.utm_source),
      utm_medium: normalizeValue(parsed.utm_medium),
      utm_campaign: normalizeValue(parsed.utm_campaign),
      utm_content: normalizeValue(parsed.utm_content),
      utm_term: normalizeValue(parsed.utm_term),
    };
  } catch {
    return emptyAttribution();
  }
}

/**
 * First-touch: persist URL attribution only when the cookie is not set yet.
 * Returns the stored attribution after this call.
 */
export function captureSignupAttributionFromSearchParams(searchParams) {
  const fromUrl = readAttributionFromSearchParams(searchParams);
  const existing = getStoredSignupAttribution();

  if (hasAttribution(existing)) {
    return existing;
  }

  if (!hasAttribution(fromUrl)) {
    return existing;
  }

  const payload = {
    ...fromUrl,
    capturedAt: new Date().toISOString(),
  };
  writeCookie(SIGNUP_ATTRIBUTION_COOKIE, JSON.stringify(payload), COOKIE_MAX_AGE_DAYS);
  return fromUrl;
}

/**
 * Effective attribution for checkout:
 * 1. First-party cookie (original session / LP landing UTMs)
 * 2. Current page URL
 * 3. Locked link defaults from signupLink API
 */
export function getEffectiveSignupAttribution(searchParams, linkDefaults = {}) {
  const fromCookie = getStoredSignupAttribution();
  if (hasAttribution(fromCookie)) {
    return fromCookie;
  }

  const fromUrl = readAttributionFromSearchParams(searchParams);
  if (hasAttribution(fromUrl)) {
    return fromUrl;
  }

  return {
    utm_source: normalizeValue(linkDefaults.utm_source ?? linkDefaults.utmSource),
    utm_medium: normalizeValue(linkDefaults.utm_medium ?? linkDefaults.utmMedium),
    utm_campaign: normalizeValue(linkDefaults.utm_campaign ?? linkDefaults.utmCampaign),
    utm_content: normalizeValue(linkDefaults.utm_content ?? linkDefaults.utmContent),
    utm_term: normalizeValue(linkDefaults.utm_term ?? linkDefaults.utmTerm),
  };
}

/** Merge attribution into a checkout/API payload (utm_* keys). */
export function appendSignupAttributionToPayload(payload, searchParams, linkDefaults = {}) {
  const attribution = getEffectiveSignupAttribution(searchParams, linkDefaults);
  if (attribution.utm_source) payload.utm_source = attribution.utm_source;
  if (attribution.utm_medium) payload.utm_medium = attribution.utm_medium;
  if (attribution.utm_campaign) payload.utm_campaign = attribution.utm_campaign;
  if (attribution.utm_content) payload.utm_content = attribution.utm_content;
  if (attribution.utm_term) payload.utm_term = attribution.utm_term;
  return payload;
}

/** True when URL carries external paid/social UTMs (not baked-in P1 link defaults). */
export function isExternalAttribution(attribution) {
  if (!hasAttribution(attribution)) return false;
  if (
    attribution.utm_source &&
    !LINK_DEFAULT_SOURCES.has(attribution.utm_source.toLowerCase())
  ) {
    return true;
  }
  if (attribution.utm_medium && attribution.utm_medium !== "promotional_landing_page") {
    return true;
  }
  return false;
}
